import React, { useState, useEffect } from 'react';
import './Dropdown.css';
import LoadingOverlay from '../components/LoadingOverlay';
import { getAuthToken } from '../utils/auth';
import BASE_URL from '../utils/api';

function MobileAddListener({ onOpen }) {
  useEffect(() => {
    window.addEventListener('open-mobile-add-popup', onOpen);
    return () => window.removeEventListener('open-mobile-add-popup', onOpen);
  }, [onOpen]);
  return null;
}

const BASE_URLS = [BASE_URL];

async function apiFetch(endpoint, options = {}) {
  const token = getAuthToken();
  const optionsWithAuth = { ...options, headers: { ...(options.headers || {}) } };
  if (token) optionsWithAuth.headers['Authorization'] = 'Bearer ' + token;
  let lastError = null;
  for (const base of BASE_URLS) {
    try {
      const url = base + endpoint;
      const resp = await fetch(url, optionsWithAuth);
      if (resp.ok || (resp.status >= 200 && resp.status < 300)) {
        const ct = resp.headers.get('Content-Type') || '';
        if (ct.includes('application/json')) {
          try { return await resp.json(); } catch { return { success: true, status: resp.status }; }
        }
        return await resp.text();
      } else {
        const errorText = await resp.text().catch(() => `Status ${resp.status}`);
        lastError = `${url} - ${errorText}`;
        console.error(`Erro na URL ${url}:`, errorText);
      }
    } catch (err) {
      lastError = `${base + endpoint} - ${err.message}`;
      console.error(`Erro ao conectar com ${base + endpoint}:`, err);
    }
  }
  throw new Error(`API indisponível. Último erro: ${lastError}`);
}

const Campo = ({ label, value }) => (
  <div style={{ minWidth: 160, padding: '8px 12px', background: 'linear-gradient(135deg, #f5f5f5, #fff)', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
    <div style={{ fontSize: 11, color: '#999', marginBottom: 4, fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: 14, color: '#333', fontWeight: 500 }}>{value || '—'}</div>
  </div>
);

export default function PessoasDropdown() {
  const [pessoas, setPessoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroTipo, setFiltroTipo] = useState('disponivel'); // Filtro padrão
  const [filtroValor, setFiltroValor] = useState('');
  const [detalheAberto, setDetalheAberto] = useState(false);
  const [editarAberto, setEditarAberto] = useState(false);
  const [criarAberto, setCriarAberto] = useState(false);
  const [selecionado, setSelecionado] = useState(null);

  const [form, setForm] = useState({
    nome: '',
    custoHora: ''
  });

  const [novoForm, setNovoForm] = useState({
    nome: '',
    custoHora: ''
  });

  useEffect(() => {
    carregarPessoas();
  }, []);

  useEffect(() => {
    const handler = () => {
      setDetalheAberto(false);
      setEditarAberto(false);
      setCriarAberto(false);
      setSelecionado(null);
      setNovoForm({ nome: '', custoHora: '' });
    };
    window.addEventListener('pessoas-fechar-todos-popups', handler);
    return () => window.removeEventListener('pessoas-fechar-todos-popups', handler);
  }, []);

  const carregarPessoas = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/Pessoas');
      setPessoas(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message);
      setPessoas([]);
    } finally {
      setLoading(false);
    }
  };

  const getNomePessoa = (pessoa) => pessoa?.nome || pessoa?.Nome || pessoa?.dadosUsuario?.nomeUsuario || pessoa?.DadosUsuario?.NomeUsuario || pessoa?.usuarioSistema?.login || pessoa?.UsuarioSistema?.Login || 'Sem nome';

  const abrirDetalhe = (resp) => {
    setSelecionado(resp);
    setDetalheAberto(true);
  };

  const abrirEditar = (pessoa) => {
    setSelecionado(pessoa);
    setForm({
      nome: pessoa.nome || pessoa.Nome || '',
      custoHora: pessoa.custoHora || ''
    });
    setEditarAberto(true);
  };

  const salvarEdicao = async () => {
    if (!selecionado) return;
    try {
      if (!form.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }
      setLoading(true);
      const custoHora = parseFloat(form.custoHora) || 0;

      await apiFetch(`/Pessoas/${selecionado.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: selecionado.id,
          nome: form.nome.trim(),
          usuarioSistemaId: selecionado.usuarioSistemaId || selecionado.UsuarioSistemaId || null,
          custoHora: custoHora
        })
      });
      alert('Pessoa atualizada com sucesso!');
      setEditarAberto(false);
      carregarPessoas();
    } catch (err) {
      alert('Erro ao atualizar: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const abrirCriar = () => {
    setNovoForm({
      nome: '',
      custoHora: ''
    });
    setCriarAberto(true);
  };

  const criarPessoa = async () => {
    try {
      setLoading(true);
      if (!novoForm.nome.trim()) {
        alert('O nome é obrigatório');
        return;
      }

      const payload = {
        nome: novoForm.nome.trim(),
        custoHora: novoForm.custoHora ? parseFloat(novoForm.custoHora) : 0
      };

      console.log('Payload sendo enviado:', payload);

      await apiFetch('/Pessoas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      alert('Pessoa criada com sucesso!');
      setCriarAberto(false);
      carregarPessoas();
    } catch (err) {
      alert('Erro ao criar pessoa: ' + err.message);
      console.error('Erro completo:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtradas = pessoas.filter((p) => {
    if (!filtroValor.trim()) return true;
    const val = filtroValor.toLowerCase();
    if (filtroTipo === 'nome') {
      return getNomePessoa(p).toLowerCase().includes(val);
    }
    return true;
  });

  const custoHoraMedio = filtradas.length > 0 
    ? filtradas.reduce((acc, p) => acc + (parseFloat(p.custoHora) || 0), 0) / filtradas.length 
    : 0;

  return (
    <div className="dropdown-container">
      <MobileAddListener onOpen={abrirCriar} />
      <div className="dropdown-header">
        <h2 style={{ margin: 0 }}>Pessoas</h2>
      </div>

      {/* Botão Nova Pessoa */}
      <div style={{ padding: '15px 20px', display: 'flex', justifyContent: 'center' }}>
        <button
          className="btn-nova-pessoa"
          onClick={abrirCriar}
          style={{
            padding: '12px 24px',
            backgroundColor: 'rgb(235, 133, 0)',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = 'rgb(208, 116, 0)'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'rgb(235, 133, 0)'}
        >
          Nova Pessoa
        </button>
      </div>

      {/* Filtros */}
      <div className="dropdown-filter-row pessoas-filtro-row" style={{ display: 'flex', gap: 10, padding: '10px 20px', borderBottom: '1px solid #ddd', background: '#fafafa' }}>
        <select 
          value={filtroTipo} 
          onChange={(e) => setFiltroTipo(e.target.value)} 
          style={{ 
            padding: '10px 16px', 
            borderRadius: 6, 
            border: 'none', 
            fontSize: 14, 
            backgroundColor: 'rgb(235, 133, 0)', 
            color: 'white', 
            fontWeight: 600,
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <option value="nome" style={{ backgroundColor: 'white', color: '#333' }}>Nome</option>
        </select>
        <input
          type="text"
          placeholder="Buscar por nome..."
          value={filtroValor}
          onChange={(e) => setFiltroValor(e.target.value)}
          style={{ 
            flex: 1, 
            padding: 8, 
            borderRadius: 6, 
            border: '1px solid #ccc', 
            fontSize: 14
          }}
        />
      </div>

      {/* Estatísticas */}
      <div className="pessoas-estatisticas" style={{ padding: '15px 20px', background: 'linear-gradient(135deg, rgb(235, 133, 0), #ffd500)', color: '#fff', display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Total Pessoas</div>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>{filtradas.length}</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 13, opacity: 0.9 }}>Custo/Hora Médio</div>
          <div style={{ fontSize: 24, fontWeight: 'bold' }}>
            {custoHoraMedio.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </div>
        </div>
      </div>

      {/* Loading/Error */}
      {loading && <div style={{ textAlign: 'center', padding: 40 }}>Carregando...</div>}
      {error && <div style={{ textAlign: 'center', padding: 40, color: 'red' }}>Erro: {error}</div>}

      {/* Lista */}
      {!loading && !error && (
        <div className="pessoas-cards-container">
          {(!filtradas || filtradas.length === 0) ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666', background: 'white', marginTop: 10 }}>
              {filtroValor ? 'Nenhuma pessoa encontrada para o filtro' : 'Nenhuma pessoa cadastrada'}
            </div>
          ) : (
            filtradas.map((p, idx) => {
              return (
                <div className="pessoas-card" key={p.id || idx}>
                  <div className="pessoas-card-content">
                    <div className="pessoas-card-fields">
                      <div className="pessoas-field pessoas-field-id">
                        <span className="pessoas-field-label">ID:</span>
                        <span className="pessoas-field-value">{p.id}</span>
                      </div>
                      <div className="pessoas-field pessoas-field-nome">
                        <span className="pessoas-field-label">Nome:</span>
                        <span className="pessoas-field-value">{getNomePessoa(p)}</span>
                      </div>
                      <div className="pessoas-field pessoas-field-custo">
                        <span className="pessoas-field-label">Custo/Hora:</span>
                        <span className="pessoas-field-value">{p.custoHora ? `R$ ${parseFloat(p.custoHora).toFixed(2)}` : '—'}</span>
                      </div>
                    </div>
                  </div>
                  <div className="pessoas-card-actions">
                    <button className="btn-detalhe-pessoas" onClick={() => abrirDetalhe(p)} title="Ver detalhes">Ver Detalhes</button>
                    <button className="btn-editar-pessoas" onClick={() => abrirEditar(p)} title="Editar">Editar</button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      {/* Modal Detalhe */}
      {detalheAberto && selecionado && (
        <div className="modal-overlay" onClick={() => setDetalheAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Pessoa - Detalhes</h3>
              <button onClick={() => setDetalheAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-pessoas-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                <input type="text" readOnly value={selecionado.id ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Custo/Hora</label>
                <input type="text" readOnly value={selecionado.custoHora ? `R$ ${parseFloat(selecionado.custoHora).toFixed(2)}` : ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Nome</label>
                <input type="text" readOnly value={getNomePessoa(selecionado)} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: 'white' }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar */}
      {editarAberto && selecionado && (
        <div className="modal-overlay" onClick={() => setEditarAberto(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: '92%', maxWidth: 900, background: '#fff', borderRadius: 12, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Editar Pessoa</h3>
              <button onClick={() => setEditarAberto(false)} aria-label="Fechar" title="Fechar" style={{ backgroundColor: 'rgb(235, 133, 0)', color: '#fff', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', boxShadow: '0 2px 6px rgba(0,0,0,0.15)' }}>×</button>
            </div>
            <div className="formulario-pessoas-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>ID</label>
                <input type="text" readOnly value={selecionado.id ?? ''} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd', backgroundColor: '#f5f5f5' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Custo/Hora (R$)</label>
                <input type="number" step="0.01" value={form.custoHora} onChange={(e) => setForm({ ...form, custoHora: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gridColumn: 'span 2' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Nome</label>
                <input type="text" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>
            </div>
            <div className="form-buttons-container-pessoas" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
              <button onClick={() => setEditarAberto(false)} className="btn-cancelar-pessoas btn-cancelar-laranja" style={{ padding: '10px 20px', borderRadius: 6, border: '1px solid #ccc', background: '#fff', cursor: 'pointer' }}>Cancelar</button>
              <button onClick={salvarEdicao} className="btn-salvar-pessoas" style={{ padding: '10px 20px', borderRadius: 6, border: 'none', background: 'rgb(235, 133, 0)', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Salvar</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Criar Nova Pessoa */}
      {criarAberto && (
        <div onClick={() => setCriarAberto(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'white', borderRadius: 12, padding: 30, width: '90%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, color: 'rgb(235, 133, 0)' }}>Criar Nova Pessoa</h3>
              <button onClick={() => setCriarAberto(false)} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#999' }}>×</button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 15 }} className="formulario-pessoas-grid">
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Nome *</label>
                <input type="text" placeholder="Nome da pessoa" value={novoForm.nome} onChange={(e) => setNovoForm({ ...novoForm, nome: e.target.value })} style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }} />
              </div>

              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <label style={{ fontSize: 13, marginBottom: 6, fontWeight: 600 }}>Custo/Hora (R$)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={novoForm.custoHora}
                  onChange={(e) => setNovoForm({ ...novoForm, custoHora: e.target.value })}
                  style={{ padding: '10px', borderRadius: 6, border: '1px solid #ddd' }}
                />
              </div>
            </div>

            <div className="form-buttons-container-pessoas" style={{ marginTop: 20, display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button
                onClick={criarPessoa}
                className="btn-salvar-pessoas"
                style={{ padding: '12px 30px', backgroundColor: 'rgb(235, 133, 0)', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Criar Pessoa
              </button>
              <button
                onClick={() => setCriarAberto(false)}
                className="btn-cancelar-pessoas"
                style={{ padding: '12px 30px', backgroundColor: '#f44336', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <LoadingOverlay isLoading={loading} message="Processando..." />
    </div>
  );
}
